package com.arenaproplay.admin.pages.events

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.AdminEventDetail
import com.arenaproplay.admin.domain.admin.AdminEventStats
import com.arenaproplay.admin.domain.admin.AdminEventTeam
import com.arenaproplay.admin.domain.admin.AdminRepository
import com.arenaproplay.admin.domain.admin.AdminUserSearchResult
import com.arenaproplay.admin.domain.admin.EventStatEntryPayload
import com.arenaproplay.admin.ui.components.admin.*
import com.varabyte.kobweb.compose.css.Cursor
import com.arenaproplay.admin.ui.components.forms.FormButton
import com.arenaproplay.admin.ui.components.forms.TextInput
import com.arenaproplay.admin.ui.components.notifications.LocalToastState
import com.arenaproplay.admin.ui.components.overlay.ConfirmActionOverlay
import com.arenaproplay.admin.ui.components.overlay.Modal
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*

@Page("/events/{id}")
@Composable
fun EventDetailsPage() {
    val repo = LocalAppContainer.current.adminRepository ?: return
    val ctx = rememberPageContext()
    val eventId = ctx.route.params["id"] ?: ctx.route.path.substringAfterLast("/")
    val loadState = rememberLoadState()
    val toast = LocalToastState.current
    val scope = rememberCoroutineScope()

    var event by remember { mutableStateOf<AdminEventDetail?>(null) }
    var pendingStatus by remember { mutableStateOf<String?>(null) }
    var statusBusy by remember { mutableStateOf(false) }
    var statusError by remember { mutableStateOf<String?>(null) }
    var participantBusy by remember { mutableStateOf<String?>(null) }
    var savingTeamId by remember { mutableStateOf<String?>(null) }
    var eventStats by remember { mutableStateOf<AdminEventStats?>(null) }
    var statsPlayerId by remember { mutableStateOf<String?>(null) }
    var statsDraft by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }
    var statsSaving by remember { mutableStateOf(false) }
    var showSaveTemplate by remember { mutableStateOf(false) }
    var templateName by remember { mutableStateOf("") }
    var templateSaving by remember { mutableStateOf(false) }
    var showCreateTeam by remember { mutableStateOf(false) }
    var teamBusy by remember { mutableStateOf(false) }
    var addPlayerTeamId by remember { mutableStateOf<String?>(null) }
    var pendingDeleteTeam by remember { mutableStateOf<AdminEventTeam?>(null) }

    fun runTeamAction(successMessage: String, action: suspend () -> Unit) {
        teamBusy = true
        scope.launch {
            try {
                action()
                toast.success(successMessage)
                loadState.retry()
            } catch (e: Throwable) {
                toast.error(e.message ?: "Не удалось выполнить действие")
            } finally {
                teamBusy = false
            }
        }
    }

    fun selectStatsPlayer(userId: String) {
        val stats = eventStats ?: return
        statsPlayerId = userId
        statsDraft = stats.statTypes
            .filter { it.scope.uppercase() == "PLAYER" }
            .associate { st ->
                st.id to (stats.playerStats.find { it.userId == userId && it.statTypeId == st.id }?.value ?: 0)
            }
    }

    fun savePlayerStats() {
        val userId = statsPlayerId ?: return
        statsSaving = true
        scope.launch {
            try {
                repo.setEventPlayerStats(
                    eventId,
                    userId,
                    statsDraft.map { (statTypeId, value) -> EventStatEntryPayload(statTypeId, value) },
                )
                toast.success("Статистика сохранена")
                runCatching { eventStats = repo.getEventStats(eventId) }
            } catch (e: Throwable) {
                toast.error(e.message ?: "Не удалось сохранить статистику")
            } finally {
                statsSaving = false
            }
        }
    }

    fun saveScore(teamId: String, score: Int) {
        savingTeamId = teamId
        scope.launch {
            try {
                repo.setEventTeamScore(eventId, teamId, score)
                toast.success("Счёт сохранён")
                loadState.retry()
            } catch (e: Throwable) {
                toast.error(e.message ?: "Не удалось сохранить счёт")
            } finally {
                savingTeamId = null
            }
        }
    }

    fun runParticipantAction(userId: String, successMessage: String, action: suspend () -> Unit) {
        participantBusy = userId
        scope.launch {
            try {
                action()
                toast.success(successMessage)
                loadState.retry()
            } catch (e: Throwable) {
                toast.error(e.message ?: "Не удалось выполнить действие")
            } finally {
                participantBusy = null
            }
        }
    }

    LaunchedEffect(eventId, loadState.reloadToken) {
        event = null
        eventStats = null
        loadState.load {
            event = repo.getEventDetail(eventId)
            val loaded = event
            if (loaded != null && loaded.isPro && loaded.status.uppercase() == "IN_PROGRESS") {
                runCatching { eventStats = repo.getEventStats(eventId) }
            }
        }
    }

    AdminLayout(
        title = event?.title ?: "Событие",
        subtitle = "Управление ивентом: заявки, статус, счёт" +
            if (event?.isPro == true) ", статистика игроков" else "",
        actions = {
            val ev = event
            if (ev != null) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    when (ev.status.uppercase()) {
                        "NEW" -> {
                            FormButton("Начать матч", enabled = true, primary = true) {
                                statusError = null; pendingStatus = "IN_PROGRESS"
                            }
                            HSpace(8)
                            FormButton("Отменить ивент", enabled = true, primary = false) {
                                statusError = null; pendingStatus = "CANCELLED"
                            }
                        }
                        "IN_PROGRESS" -> {
                            FormButton("Завершить ивент", enabled = true, primary = true) {
                                statusError = null; pendingStatus = "COMPLETED"
                            }
                            HSpace(8)
                            FormButton("Отменить ивент", enabled = true, primary = false) {
                                statusError = null; pendingStatus = "CANCELLED"
                            }
                        }
                    }
                    HSpace(8)
                    FormButton("Сохранить как шаблон", enabled = true, primary = false) {
                        templateName = ev.title
                        showSaveTemplate = true
                    }
                }
            }
        },
    ) {
        if (loadState.error != null) {
            ErrorCard(loadState)
            return@AdminLayout
        }
        val loaded = event ?: return@AdminLayout // skeleton = empty while loading (project rule)

        EventInfoCard(loaded)

        PendingRequestsSection(
            event = loaded,
            busyUserId = participantBusy,
            onApprove = { userId ->
                runParticipantAction(userId, "Заявка принята") {
                    repo.confirmEventParticipant(eventId, userId)
                }
            },
            onReject = { userId ->
                runParticipantAction(userId, "Заявка отклонена") {
                    repo.rejectEventParticipant(eventId, userId)
                }
            },
        )
        ConfirmedParticipantsSection(loaded)
        TeamCompositionSection(
            event = loaded,
            busy = teamBusy,
            onCreateTeam = { showCreateTeam = true },
            onDeleteTeam = { pendingDeleteTeam = it },
            onAddPlayer = { addPlayerTeamId = it },
            onRemoveFromTeam = { userId ->
                runTeamAction("Игрок убран из команды") {
                    repo.removeParticipantFromTeam(eventId, userId)
                }
            },
        )
        TeamsScoreSection(loaded, savingTeamId, ::saveScore)
        if (loaded.isPro && loaded.status.uppercase() == "IN_PROGRESS") {
            PlayerStatsSection(
                event = loaded,
                stats = eventStats,
                selectedUserId = statsPlayerId,
                draft = statsDraft,
                saving = statsSaving,
                onSelect = ::selectStatsPlayer,
                onDraftChange = { statsDraft = it },
                onSave = ::savePlayerStats,
            )
        }

        val overlayCopy = when (pendingStatus) {
            "IN_PROGRESS" -> Triple(
                "Начать матч?",
                "Событие перейдёт в статус «Идёт»; организатор и участники получат обновление.",
                "Участники без команды будут автоматически удалены из ивента.",
            )
            "COMPLETED" -> Triple(
                "Завершить ивент?",
                "Результаты матча запишутся в профили игроков, будут выданы награды. Действие необратимо.",
                null,
            )
            "CANCELLED" -> Triple(
                "Отменить ивент?",
                "Событие перейдёт в статус «Отменён». Действие необратимо.",
                null,
            )
            else -> null
        }
        if (overlayCopy != null) {
            ConfirmActionOverlay(
                isOpen = true,
                onClose = { if (!statusBusy) { pendingStatus = null; statusError = null } },
                title = overlayCopy.first,
                description = overlayCopy.second,
                warning = overlayCopy.third,
                error = statusError,
                confirmLabel = when (pendingStatus) {
                    "IN_PROGRESS" -> "Начать"
                    "COMPLETED" -> "Завершить"
                    else -> "Отменить ивент"
                },
                confirmDanger = pendingStatus == "CANCELLED",
                confirmEnabled = !statusBusy,
                onConfirm = {
                    val target = pendingStatus ?: return@ConfirmActionOverlay
                    statusBusy = true
                    statusError = null
                    scope.launch {
                        try {
                            repo.setEventStatus(eventId, target)
                            toast.success(
                                when (target) {
                                    "IN_PROGRESS" -> "Матч начат"
                                    "COMPLETED" -> "Ивент завершён"
                                    else -> "Ивент отменён"
                                },
                            )
                            pendingStatus = null
                            loadState.retry()
                        } catch (e: Throwable) {
                            // e.g. a race: the organizer already changed the status
                            statusError = e.message ?: "Не удалось изменить статус"
                        } finally {
                            statusBusy = false
                        }
                    }
                },
            )
        }

        if (showCreateTeam) {
            CreateTeamModal(
                usedColors = loaded.teams.mapNotNull { it.color },
                busy = teamBusy,
                onClose = { if (!teamBusy) showCreateTeam = false },
                onCreate = { name, color ->
                    runTeamAction("Команда создана") {
                        repo.createEventTeam(eventId, name, color)
                        showCreateTeam = false
                    }
                },
            )
        }

        addPlayerTeamId?.let { teamId ->
            val team = loaded.teams.find { it.id == teamId }
            AddPlayerModal(
                repo = repo,
                teamName = team?.name ?: "",
                participants = loaded.participants,
                teamId = teamId,
                busy = teamBusy,
                onClose = { if (!teamBusy) addPlayerTeamId = null },
                onAdd = { userId ->
                    runTeamAction("Игрок добавлен в команду") {
                        repo.addEventParticipant(eventId, userId, teamId)
                    }
                },
            )
        }

        pendingDeleteTeam?.let { team ->
            ConfirmActionOverlay(
                isOpen = true,
                onClose = { if (!teamBusy) pendingDeleteTeam = null },
                title = "Удалить команду «${team.name}»?",
                description = "Игроки останутся участниками ивента, но будут без команды.",
                warning = null,
                error = null,
                confirmLabel = "Удалить",
                confirmDanger = true,
                confirmEnabled = !teamBusy,
                onConfirm = {
                    runTeamAction("Команда удалена") {
                        repo.deleteEventTeam(eventId, team.id)
                        pendingDeleteTeam = null
                    }
                },
            )
        }

        Modal(
            isOpen = showSaveTemplate,
            onClose = { if (!templateSaving) showSaveTemplate = false },
            title = "Сохранить как шаблон",
        ) {
            TextInput(
                templateName,
                { templateName = it },
                label = "Название шаблона",
                placeholder = "Название шаблона",
            )
            SpanText(
                "Команды и их составы будут сохранены в шаблон.",
                Modifier.bodySMedium().color(AppColors.Muted),
            )
            Box(Modifier.height(8.px))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End,
            ) {
                FormButton(
                    "Отмена",
                    enabled = !templateSaving,
                    primary = false,
                ) { showSaveTemplate = false }
                HSpace(8)
                FormButton(
                    if (templateSaving) "Сохранение" else "Сохранить",
                    enabled = templateName.isNotBlank() && !templateSaving,
                    primary = true,
                ) {
                    templateSaving = true
                    scope.launch {
                        try {
                            repo.saveEventTemplate(eventId, templateName.trim())
                            toast.success("Шаблон сохранён")
                            showSaveTemplate = false
                        } catch (e: Throwable) {
                            toast.error(e.message ?: "Не удалось сохранить шаблон")
                        } finally {
                            templateSaving = false
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun EventInfoCard(event: AdminEventDetail) {
    SectionCard(
        title = "Основная информация",
        modifier = Modifier.fillMaxWidth(),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Pill(statusLabel(event.status), statusTone(event.status))
            HSpace(8)
            Pill(event.customSportName ?: event.sportName, PillTone.Accent)
            if (event.isPro) {
                HSpace(8)
                Pill("PRO", PillTone.Pro)
            }
        }
        Box(Modifier.height(10.px))
        SpanText(
            listOfNotNull(
                event.city,
                event.address,
                event.dateTime.take(16).replace("T", " "),
            ).joinToString(" · "),
            Modifier.bodyMRegular().color(AppColors.Muted),
        )
        Box(Modifier.height(4.px))
        SpanText(
            "Участников: ${event.participants.count { it.status.uppercase() == "CONFIRMED" }}" +
                (event.maxPlayers?.let { " / $it" } ?: ""),
            Modifier.bodyMRegular().color(AppColors.Muted),
        )
    }
}

internal fun statusLabel(status: String): String = when (status.uppercase()) {
    "NEW" -> "Новый"
    "IN_PROGRESS" -> "Идёт"
    "COMPLETED" -> "Завершён"
    "CANCELLED" -> "Отменён"
    else -> status
}

internal fun statusTone(status: String): PillTone = when (status.uppercase()) {
    "NEW" -> PillTone.Accent
    "IN_PROGRESS" -> PillTone.Success
    "COMPLETED" -> PillTone.Neutral
    "CANCELLED" -> PillTone.Danger
    else -> PillTone.Neutral
}

@Composable
private fun PendingRequestsSection(
    event: AdminEventDetail,
    busyUserId: String?,
    onApprove: (String) -> Unit,
    onReject: (String) -> Unit,
) {
    val pending = event.participants.filter { it.status.uppercase() == "PENDING" }
    val actionable = event.status.uppercase() == "NEW"
    SectionCard {
        SpanText("Заявки (${pending.size})", Modifier.bodyMMedium().color(AppColors.Ink))
        Box(Modifier.height(10.px))
        if (pending.isEmpty()) {
            SpanText("Нет заявок в ожидании", Modifier.bodyMRegular().color(AppColors.Muted))
        }
        pending.forEach { p ->
            Row(
                Modifier.fillMaxWidth().padding(topBottom = 6.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(Modifier.weight(1f)) {
                    SpanText(p.fullName ?: p.username, Modifier.bodyMMedium().color(AppColors.Ink))
                    SpanText("@${p.username}", Modifier.bodySMedium().color(AppColors.Muted))
                }
                if (actionable) {
                    FormButton("Принять", enabled = busyUserId == null, primary = true) { onApprove(p.userId) }
                    HSpace(8)
                    FormButton("Отклонить", enabled = busyUserId == null, primary = false) { onReject(p.userId) }
                } else {
                    SpanText("Доступно только для нового ивента", Modifier.bodySMedium().color(AppColors.Faint))
                }
            }
        }
    }
}

@Composable
private fun ConfirmedParticipantsSection(event: AdminEventDetail) {
    val confirmed = event.participants.filter { it.status.uppercase() == "CONFIRMED" }
    val teamNames = event.teams.associate { it.id to it.name }
    SectionCard {
        SpanText("Участники (${confirmed.size})", Modifier.bodyMMedium().color(AppColors.Ink))
        Box(Modifier.height(10.px))
        if (confirmed.isEmpty()) {
            SpanText("Пока нет подтверждённых участников", Modifier.bodyMRegular().color(AppColors.Muted))
        }
        confirmed.forEach { p ->
            Row(
                Modifier.fillMaxWidth().padding(topBottom = 6.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(Modifier.weight(1f)) {
                    SpanText(p.fullName ?: p.username, Modifier.bodyMMedium().color(AppColors.Ink))
                    SpanText("@${p.username}", Modifier.bodySMedium().color(AppColors.Muted))
                }
                if (p.isAdmin) {
                    Pill("Организатор", PillTone.Warning)
                    HSpace(8)
                }
                SpanText(
                    p.eventTeamId?.let { teamNames[it] } ?: "Без команды",
                    Modifier.bodySMedium().color(AppColors.Muted),
                )
            }
        }
    }
}

@Composable
private fun PlayerStatsSection(
    event: AdminEventDetail,
    stats: AdminEventStats?,
    selectedUserId: String?,
    draft: Map<String, Int>,
    saving: Boolean,
    onSelect: (String) -> Unit,
    onDraftChange: (Map<String, Int>) -> Unit,
    onSave: () -> Unit,
) {
    val players = event.participants.filter { it.status.uppercase() == "CONFIRMED" }
    SectionCard {
        SpanText("Статистика игроков (PRO)", Modifier.bodyMMedium().color(AppColors.Ink))
        Box(Modifier.height(10.px))
        if (stats == null || players.isEmpty()) {
            SpanText(
                if (players.isEmpty()) "Нет подтверждённых игроков" else "Стат-типы недоступны",
                Modifier.bodyMRegular().color(AppColors.Muted),
            )
            return@SectionCard
        }
        // Player picker — pill-style clickable rows
        players.forEach { p ->
            val selected = p.userId == selectedUserId
            Row(
                Modifier.fillMaxWidth().padding(topBottom = 4.px)
                    .cursor(Cursor.Pointer)
                    .onClick { onSelect(p.userId) },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    p.fullName ?: p.username,
                    Modifier.bodyMRegular().color(if (selected) AppColors.Ink else AppColors.Muted),
                )
                if (selected) {
                    HSpace(8)
                    Pill("Выбран", PillTone.Accent)
                }
            }
        }
        selectedUserId ?: return@SectionCard
        Box(Modifier.height(12.px))
        stats.statTypes
            .filter { it.scope.uppercase() == "PLAYER" }
            .sortedBy { it.displayOrder }
            .forEach { st ->
                val value = draft[st.id] ?: 0
                Row(
                    Modifier.fillMaxWidth().padding(topBottom = 4.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(Modifier.weight(1f)) {
                        SpanText(st.name, Modifier.bodyMRegular().color(AppColors.Ink))
                    }
                    FormButton("−", enabled = value > 0 && !saving, primary = false) {
                        onDraftChange(draft + (st.id to value - 1))
                    }
                    HSpace(10)
                    SpanText(
                        "$value",
                        Modifier.bodyMMedium().color(AppColors.Ink)
                            .styleModifier { property("font-variant-numeric", "tabular-nums") },
                    )
                    HSpace(10)
                    FormButton("+", enabled = !saving, primary = false) {
                        onDraftChange(draft + (st.id to value + 1))
                    }
                }
            }
        Box(Modifier.height(10.px))
        FormButton(if (saving) "Сохранение" else "Сохранить статистику", enabled = !saving, primary = true) {
            onSave()
        }
    }
}

/** Same palette as the public site's CreateTeamPopup — keeps colors consistent. */
private val TeamPalette = listOf(
    "#050505", // near-black
    "#290001", // dark red
    "#0D184A", // dark blue
    "#001624", // teal
    "#A07B00", // gold
    "#B9BEB9", // light gray
)

@Composable
private fun TeamCompositionSection(
    event: AdminEventDetail,
    busy: Boolean,
    onCreateTeam: () -> Unit,
    onDeleteTeam: (AdminEventTeam) -> Unit,
    onAddPlayer: (String) -> Unit,
    onRemoveFromTeam: (String) -> Unit,
) {
    val editable = event.status.uppercase() in setOf("NEW", "IN_PROGRESS")
    SectionCard {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Column(Modifier.weight(1f)) {
                SpanText("Команды и составы (${event.teams.size})", Modifier.bodyMMedium().color(AppColors.Ink))
            }
            if (editable) {
                FormButton("Создать команду", enabled = !busy, primary = true) { onCreateTeam() }
            }
        }
        Box(Modifier.height(10.px))
        if (event.teams.isEmpty()) {
            SpanText("Команды ещё не созданы", Modifier.bodyMRegular().color(AppColors.Muted))
        }
        event.teams.forEach { team ->
            val members = event.participants.filter {
                it.eventTeamId == team.id && it.status.uppercase() == "CONFIRMED"
            }
            Column(Modifier.fillMaxWidth().padding(topBottom = 8.px)) {
                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    team.color?.let { c ->
                        Box(
                            Modifier.width(12.px).height(12.px).borderRadius(50.percent)
                                .border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.25))
                                .styleModifier { property("background", c) },
                        )
                        HSpace(8)
                    }
                    Column(Modifier.weight(1f)) {
                        SpanText("${team.name} · ${members.size}", Modifier.bodyMMedium().color(AppColors.Ink))
                    }
                    if (editable) {
                        FormButton("+ Игрок", enabled = !busy, primary = false) { onAddPlayer(team.id) }
                        HSpace(8)
                        FormButton("Удалить", enabled = !busy, primary = false) { onDeleteTeam(team) }
                    }
                }
                if (members.isEmpty()) {
                    Box(Modifier.height(4.px))
                    SpanText("Состав пуст", Modifier.bodySMedium().color(AppColors.Faint))
                }
                members.forEach { m ->
                    Row(
                        Modifier.fillMaxWidth().padding(topBottom = 4.px).paddingInline(start = 20.px),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Column(Modifier.weight(1f)) {
                            SpanText(m.fullName ?: m.username, Modifier.bodyMRegular().color(AppColors.Ink))
                            SpanText("@${m.username}", Modifier.bodySMedium().color(AppColors.Muted))
                        }
                        if (editable) {
                            FormButton("Убрать", enabled = !busy, primary = false) { onRemoveFromTeam(m.userId) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CreateTeamModal(
    usedColors: List<String>,
    busy: Boolean,
    onClose: () -> Unit,
    onCreate: (name: String, color: String?) -> Unit,
) {
    val taken = usedColors.map { it.trim().lowercase() }.toSet()
    val available = TeamPalette.filter { it.lowercase() !in taken }
    var name by remember { mutableStateOf("") }
    var color by remember { mutableStateOf(available.firstOrNull()) }

    Modal(isOpen = true, onClose = onClose, title = "Создать команду") {
        TextInput(name, { name = it }, label = "Название команды", placeholder = "Например, Синие")
        Box(Modifier.height(10.px))
        SpanText("Цвет", Modifier.bodySMedium().color(AppColors.Muted))
        Box(Modifier.height(6.px))
        Row(verticalAlignment = Alignment.CenterVertically) {
            available.forEach { c ->
                val selected = c == color
                Box(
                    Modifier.width(26.px).height(26.px).borderRadius(50.percent)
                        .margin(right = 8.px)
                        .cursor(Cursor.Pointer)
                        .border(
                            2.px,
                            LineStyle.Solid,
                            if (selected) AppColors.Ink else rgba(255, 255, 255, 0.2),
                        )
                        .styleModifier { property("background", c) }
                        .onClick { color = c },
                )
            }
        }
        Box(Modifier.height(14.px))
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            FormButton("Отмена", enabled = !busy, primary = false) { onClose() }
            HSpace(8)
            FormButton(
                if (busy) "Создание" else "Создать",
                enabled = name.isNotBlank() && !busy,
                primary = true,
            ) { onCreate(name.trim(), color) }
        }
    }
}

@Composable
private fun AddPlayerModal(
    repo: AdminRepository,
    teamName: String,
    participants: List<com.arenaproplay.admin.domain.admin.AdminEventParticipant>,
    teamId: String,
    busy: Boolean,
    onClose: () -> Unit,
    onAdd: (String) -> Unit,
) {
    var query by remember { mutableStateOf("") }
    var results by remember { mutableStateOf<List<AdminUserSearchResult>>(emptyList()) }
    var searching by remember { mutableStateOf(false) }

    LaunchedEffect(query) {
        val q = query.trim()
        if (q.length < 2) {
            results = emptyList()
            return@LaunchedEffect
        }
        kotlinx.coroutines.delay(300)
        searching = true
        runCatching { results = repo.searchUsers(q) }
        searching = false
    }

    val byUserId = participants.associateBy { it.userId }

    Modal(isOpen = true, onClose = onClose, title = "Добавить игрока в «$teamName»") {
        TextInput(query, { query = it }, label = null, placeholder = "Поиск по нику или имени")
        Box(Modifier.height(10.px))
        val unassigned = participants.filter { it.eventTeamId == null && it.status.uppercase() == "CONFIRMED" }
        if (query.trim().length < 2 && unassigned.isNotEmpty()) {
            SpanText("Участники без команды", Modifier.bodySMedium().color(AppColors.Muted))
            Box(Modifier.height(4.px))
            unassigned.forEach { p ->
                Row(
                    Modifier.fillMaxWidth().padding(topBottom = 4.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(Modifier.weight(1f)) {
                        SpanText(p.fullName ?: p.username, Modifier.bodyMRegular().color(AppColors.Ink))
                        SpanText("@${p.username}", Modifier.bodySMedium().color(AppColors.Muted))
                    }
                    FormButton("Добавить", enabled = !busy, primary = true) { onAdd(p.userId) }
                }
            }
        }
        results.forEach { u ->
            val existing = byUserId[u.id]
            val inThisTeam = existing?.eventTeamId == teamId
            Row(
                Modifier.fillMaxWidth().padding(topBottom = 4.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(Modifier.weight(1f)) {
                    SpanText(u.fullName ?: u.username, Modifier.bodyMRegular().color(AppColors.Ink))
                    SpanText("@${u.username}", Modifier.bodySMedium().color(AppColors.Muted))
                }
                when {
                    inThisTeam -> SpanText("В команде", Modifier.bodySMedium().color(AppColors.Faint))
                    existing?.eventTeamId != null ->
                        FormButton("Перевести", enabled = !busy, primary = false) { onAdd(u.id) }
                    else -> FormButton("Добавить", enabled = !busy, primary = true) { onAdd(u.id) }
                }
            }
        }
        if (query.trim().length >= 2 && !searching && results.isEmpty()) {
            SpanText("Никого не нашли", Modifier.bodyMRegular().color(AppColors.Muted))
        }
    }
}

@Composable
private fun TeamsScoreSection(
    event: AdminEventDetail,
    savingTeamId: String?,
    onSave: (String, Int) -> Unit,
) {
    SectionCard {
        SpanText("Команды и счёт", Modifier.bodyMMedium().color(AppColors.Ink))
        Box(Modifier.height(10.px))
        if (event.teams.isEmpty()) {
            SpanText("Команды ещё не созданы", Modifier.bodyMRegular().color(AppColors.Muted))
        }
        event.teams.forEach { team ->
            var draft by remember(team.id, team.score) { mutableStateOf(team.score) }
            Row(
                Modifier.fillMaxWidth().padding(topBottom = 8.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                team.color?.let { c ->
                    Box(
                        Modifier.width(12.px).height(12.px).borderRadius(50.percent)
                            .styleModifier { property("background", c) },
                    )
                    HSpace(8)
                }
                Column(Modifier.weight(1f)) {
                    SpanText(team.name, Modifier.bodyMMedium().color(AppColors.Ink))
                }
                FormButton("−", enabled = draft > 0 && savingTeamId == null, primary = false) { draft-- }
                HSpace(10)
                SpanText(
                    "$draft",
                    Modifier.bodyMMedium().color(AppColors.Ink)
                        .styleModifier { property("font-variant-numeric", "tabular-nums") },
                )
                HSpace(10)
                FormButton("+", enabled = savingTeamId == null, primary = false) { draft++ }
                HSpace(12)
                FormButton(
                    if (savingTeamId == team.id) "Сохранение" else "Сохранить",
                    enabled = draft != team.score && savingTeamId == null,
                    primary = true,
                ) { onSave(team.id, draft) }
            }
        }
    }
}
